import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
    Button,
    Container,
    ListSubheader,
    Menu,
    MenuItem,
    Typography
} from '@mui/material'
import PropTypes from 'prop-types'
import { useId, useState } from 'react'
import { Link } from 'react-router-dom'
import useAuth from '@/hooks/useAuth'
import { NavEntryBadge } from '../NavBadgeCount'

/**
 * Small-screen admin nav: a "where am I" button above the page that opens the
 * same grouped list the desktop side nav shows.
 */
export default function AdminNavMenu({ area, groups, activeItem, onOverview }) {
    const { settings } = useAuth()
    const [anchorEl, setAnchorEl] = useState(null)
    const menuId = useId()
    const close = () => setAnchorEl(null)
    const current = onOverview ? 'Overview' : (activeItem?.label ?? 'Pages')

    return (
        <Container
            maxWidth={settings?.style?.containerStretch ? false : 'xl'}
            sx={{ pt: 2 }}
        >
            <Button
                variant="outlined"
                color="inherit"
                size="small"
                endIcon={<ExpandMoreIcon />}
                onClick={(event) => setAnchorEl(event.currentTarget)}
                aria-haspopup="menu"
                aria-expanded={Boolean(anchorEl)}
                aria-controls={anchorEl ? menuId : undefined}
                sx={{ textTransform: 'none', borderColor: 'divider' }}
            >
                <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                    sx={{ mr: 0.75 }}
                >
                    {area.title} ·
                </Typography>
                {current}
            </Button>
            <Menu
                id={menuId}
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={close}
                slotProps={{
                    paper: { sx: { maxHeight: '70vh', minWidth: 260 } }
                }}
            >
                <MenuItem
                    component={Link}
                    to={area.rootPath}
                    selected={onOverview}
                    onClick={close}
                >
                    {area.overviewLabel}
                </MenuItem>
                {groups.flatMap((group) => [
                    <ListSubheader
                        key={`h-${group.id}`}
                        sx={{ lineHeight: 2.5 }}
                    >
                        {group.title}
                    </ListSubheader>,
                    ...group.items.map((item) => (
                        <MenuItem
                            key={item.id}
                            component={Link}
                            to={item.path}
                            selected={!onOverview && item.id === activeItem?.id}
                            onClick={close}
                        >
                            {item.label}
                            {<NavEntryBadge item={item} />}
                        </MenuItem>
                    ))
                ])}
            </Menu>
        </Container>
    )
}

AdminNavMenu.propTypes = {
    area: PropTypes.shape({
        overviewLabel: PropTypes.string.isRequired,
        rootPath: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired
    }).isRequired,
    activeItem: PropTypes.shape({
        id: PropTypes.string,
        label: PropTypes.string
    }),
    groups: PropTypes.array.isRequired,
    onOverview: PropTypes.bool.isRequired
}
